import React, { ReactNode } from 'react';
import { 
  Home, 
  ChevronRight, 
  Filter, 
  Calendar, 
  Search,
  Download,
  RefreshCw,
  Settings
} from 'lucide-react';

interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: ReactNode;
}

interface FilterOption {
  key: string;
  label: string;
  type: 'select' | 'date' | 'search' | 'toggle';
  options?: { value: string; label: string }[];
  value?: any;
  onChange?: (value: any) => void;
}

interface DashboardLayoutProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  filters?: FilterOption[];
  showRefresh?: boolean;
  showExport?: boolean;
  showSettings?: boolean;
  onRefresh?: () => void;
  onExport?: () => void;
  onSettings?: () => void;
  children: ReactNode;
  className?: string;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  title,
  subtitle,
  breadcrumbs = [],
  filters = [],
  showRefresh = true,
  showExport = false,
  showSettings = false,
  onRefresh,
  onExport,
  onSettings,
  children,
  className = ''
}) => {
  const [filtersVisible, setFiltersVisible] = React.useState(false);
  const [filterValues, setFilterValues] = React.useState<Record<string, any>>({});

  const handleFilterChange = (key: string, value: any) => {
    setFilterValues(prev => ({ ...prev, [key]: value }));
    const filter = filters.find(f => f.key === key);
    if (filter?.onChange) {
      filter.onChange(value);
    }
  };

  const renderFilterInput = (filter: FilterOption) => {
    const value = filterValues[filter.key] || filter.value || '';

    switch (filter.type) {
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleFilterChange(filter.key, e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          >
            <option value="">All {filter.label}</option>
            {filter.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      
      case 'date':
        return (
          <div className="relative">
            <input
              type="date"
              value={value}
              onChange={(e) => handleFilterChange(filter.key, e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          </div>
        );
      
      case 'search':
        return (
          <div className="relative">
            <input
              type="text"
              placeholder={`Search ${filter.label.toLowerCase()}...`}
              value={value}
              onChange={(e) => handleFilterChange(filter.key, e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-64"
            />
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          </div>
        );
      
      case 'toggle':
        return (
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={value}
              onChange={(e) => handleFilterChange(filter.key, e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">{filter.label}</span>
          </label>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen bg-gray-50 ${className}`}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          {breadcrumbs.length > 0 && (
            <nav className="flex items-center space-x-2 py-3 text-sm">
              <Home className="h-4 w-4 text-gray-400" />
              {breadcrumbs.map((item, index) => (
                <React.Fragment key={index}>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                  {item.href ? (
                    <a
                      href={item.href}
                      className="text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </a>
                  ) : (
                    <span className="text-gray-500 flex items-center space-x-1">
                      {item.icon}
                      <span>{item.label}</span>
                    </span>
                  )}
                </React.Fragment>
              ))}
            </nav>
          )}

          {/* Title and Actions */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between py-6">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl sm:truncate">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-1 text-sm text-gray-500 sm:truncate">
                  {subtitle}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="mt-4 flex items-center space-x-3 lg:mt-0 lg:ml-4">
              {filters.length > 0 && (
                <button
                  onClick={() => setFiltersVisible(!filtersVisible)}
                  className={`inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                    filtersVisible ? 'bg-gray-100' : ''
                  }`}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                </button>
              )}

              {showRefresh && (
                <button
                  onClick={onRefresh}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </button>
              )}

              {showExport && (
                <button
                  onClick={onExport}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </button>
              )}

              {showSettings && (
                <button
                  onClick={onSettings}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </button>
              )}
            </div>
          </div>

          {/* Filters Panel */}
          {filtersVisible && filters.length > 0 && (
            <div className="pb-6">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filters.map((filter) => (
                    <div key={filter.key} className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        {filter.label}
                      </label>
                      {renderFilterInput(filter)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1">
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout; 